import React, { useRef } from 'react';
import {
  TextField,
  ReferenceField,
  FunctionField,
  useRefresh,
  usePermissions,
  Datagrid,
  ResourceContextProvider,
} from 'react-admin';
import { useHistory } from 'react-router-dom';

import { Avatar, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import AddToPhotosIcon from '@material-ui/icons/AddToPhotos';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import ImageIcon from '@material-ui/icons/Image';
import PrintIcon from '@material-ui/icons/Print';
import ReceiptIcon from '@material-ui/icons/Receipt';
import SearchIcon from '@material-ui/icons/Search';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { useSnackbar } from 'notistack';

import { DialogoConfirmacao } from '../../Components';
import DialogoPrecificacao from '../../Components/Dialogos/DialogoMargeamento';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageTabbedComBuscaPagina from '../../Components/ReactAdmin/List/ListPageTabbedComBuscaPagina';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { api } from '../../services';
import {
  round10,
  formatMoeda,
  getEstoqueLabel,
  POR_UNIDADE,
  POR_PESO_VARIAVEL_SEM_UNIDADE,
  POR_PESO_FIXO,
  TIPO_IMPRESSAO_UN,
  getPrecoUnitarioVerdadeiroCaixa2,
} from '../../utils';
import { STATUS_ATIVO, STATUS_INATIVO } from '../../utils/constants';
import {
  PERMISSAO_TELA_PRODUTOS_ABA_INATIVOS,
  PERMISSAO_TELA_PRODUTOS_CRIAR,
  PERMISSAO_TELA_PRODUTOS_INATIVAR,
  PERMISSAO_TELA_PRODUTOS_LISTAR,
} from '../../utils/dadosTelas';
import AreaCopiaCola from '../Produtos/AreaCopiaCola';
import ContainerDialogoImprimirCartazes from '../Produtos/ContainerDialogoImprimirCartazes';
import Dialogo from '../Produtos/ContainerDialogoProduto';
import DialogoChangeRuptura from '../Produtos/DialogoChangeRuptura';
import DialogoChangeSearch from '../Produtos/DialogoChangeSearch';
import DialogoEdicaoEmMassa from '../Produtos/DialogoEdicaoEmMassa';
import DialogoImagem from '../Produtos/DialogoImagem';
import DialogoRelatorioProdutosBalanca from '../Produtos/DialogoRelatorioProdutosBalanca';
import DialogoShowProduto from '../Produtos/DialogoShow';
import ValorField from '../Produtos/ValorField';
import DialogoTesteNfceVenda from '../Vendas/DialogoTesteNfceVenda';
import advancedFilters from './AdvancedFilters';

// Tela em teste: mesma base (produtos), mesma permissão e mesmas colunas/ações da lista
// atual (Resources/Produtos), só que usando o novo componente de tabela genérico
// (ListPageTabbedComBuscaPagina), pra comparação lado a lado com a tela antiga.
// 3 views: Completa (tudo, com o excedente de ações agrupado em "mais"), Estoque e Vendas
// (subconjuntos de colunas/ações, ver `views` abaixo).
// is_kit/is_caixa/ofertasProdutos entram tanto aqui quanto em `filterDefaultValues` (mais
// abaixo, em `listProps`): `filterDefaultValues` é quem garante que eles já vêm aplicados na
// primeira busca, e repeti-los aqui em `filter` faz o ListPageTabbedComBuscaPagina reconhecer
// essas chaves como "da tab atual" (mesma lógica que já esconde `status`) — sem isso, eles
// apareciam soltos como chip removível em "Filtros ativos", mesmo sem o usuário ter aplicado.
const FILTROS_FIXOS = { is_kit: false, is_caixa: false, ofertasProdutos: true };

const tabs = [
  {
    id: 'ativos',
    name: 'Ativos',
    filter: { status: STATUS_ATIVO, ...FILTROS_FIXOS },
  },
  {
    id: 'inativos',
    name: 'Inativos',
    filter: { status: STATUS_INATIVO, ...FILTROS_FIXOS },
  },
];

const views = [
  { id: 'geral', label: 'Completa' },
  {
    id: 'estoque',
    label: 'Estoque',
    colunasVisiveis: [
      'id',
      'nome',
      'estoque',
      'unidade_id',
      'categoria_id',
      'acoes',
    ],
    acoes: ['show', 'editar', 'ativar', 'desativar'],
  },
  {
    id: 'vendas',
    label: 'Vendas',
    colunasVisiveis: [
      'id',
      'nome',
      'preco_venda',
      'unidade_id',
      'estoque',
      'acoes',
    ],
    acoes: ['show', 'copiar'],
  },
];

const CORES_INDICADOR = { ativo: '#4caf50', inativo: '#f44336' };

const IconeStatusBusca = ({ record }) => (
  <Avatar
    style={{
      width: 24,
      height: 24,
      backgroundColor: record?.is_search
        ? CORES_INDICADOR.ativo
        : CORES_INDICADOR.inativo,
    }}
  >
    <SearchIcon style={{ fontSize: 14, color: 'white' }} />
  </Avatar>
);

const IconeStatusRuptura = ({ record }) => (
  <Avatar
    style={{
      width: 24,
      height: 24,
      backgroundColor: record?.is_ruptura
        ? CORES_INDICADOR.inativo
        : CORES_INDICADOR.ativo,
    }}
  >
    <BlockIcon style={{ fontSize: 14, color: 'white' }} />
  </Avatar>
);

function getUnidade(produto) {
  if (produto.unidade.tipo === POR_UNIDADE) return 'Un';
  if (produto.unidade.tipo === POR_PESO_VARIAVEL_SEM_UNIDADE) return 'Kg';
  if (produto.unidade.tipo === POR_PESO_FIXO) {
    if (produto.tipo_impressao === TIPO_IMPRESSAO_UN) return 'Un';
    return 'Kg';
  }
  return '';
}

function getFator(produto) {
  if (produto.unidade.tipo === POR_UNIDADE) return 1;
  if (produto.unidade.tipo === POR_PESO_VARIAVEL_SEM_UNIDADE) return 1;
  if (produto.unidade.tipo === POR_PESO_FIXO) {
    if (produto.tipo_impressao === TIPO_IMPRESSAO_UN) return produto.peso_medio;
    return 1;
  }
  return '';
}

// Sólida (sem canal alfa) de propósito: uma cor translúcida (ex: `#f44336ac`) funciona numa
// <tr> normal, mas a coluna fixada (sticky) é opaca por cima de outras colunas rolando por
// baixo dela — com transparência, esse conteúdo vaza através da coluna fixada e a cor fica
// inconsistente com o resto da linha. `#f88077` é o equivalente sólido de `#f44336ac` sobre
// fundo branco (mesmo tom visual de antes, sem depender de transparência).
const COR_ESTOQUE_ZERADO = '#f88077';

const postRowStyle = (record) => {
  const produto = record;
  if (produto) {
    if (
      produto.unidade.tipo === POR_UNIDADE ||
      produto.unidade.tipo === POR_PESO_FIXO
    ) {
      if (produto.unidadesDisponivel <= 0) {
        return { backgroundColor: COR_ESTOQUE_ZERADO };
      }
    }

    if (produto.unidade.tipo === POR_PESO_VARIAVEL_SEM_UNIDADE) {
      if (produto.pesoDisponivel <= 0) {
        return { backgroundColor: COR_ESTOQUE_ZERADO };
      }
    }
  }

  return { backgroundColor: 'white' };
};

// células mais compactas que o padrão do MUI (6px 24px 6px 16px), inclusive a de ações
const useStyles = makeStyles({
  tabelaCompacta: {
    '& .MuiTableCell-root': {
      padding: '4px 8px',
    },
    // descrição quebra em mais linhas em vez de alargar a tabela
    '& .MuiTableCell-root.column-nome': {
      whiteSpace: 'normal',
      minWidth: 180,
      maxWidth: 260,
    },
  },
});

const MyList = ({ basePath }) => {
  const classes = useStyles();
  const refresh = useRefresh();
  // criar, inativar e ver a aba "Inativos" dependem de permissão própria.
  const { permissions } = usePermissions();
  const podeCriar = Boolean(
    permissions && permissions[PERMISSAO_TELA_PRODUTOS_CRIAR],
  );
  const podeInativar = Boolean(
    permissions && permissions[PERMISSAO_TELA_PRODUTOS_INATIVAR],
  );
  const podeVerInativos = Boolean(
    permissions && permissions[PERMISSAO_TELA_PRODUTOS_ABA_INATIVOS],
  );
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const refDialogoConfirmacao = useRef(null);
  const refDialogoCompra = useRef(null);
  const refDialogoChangeStatusSearch = useRef(null);
  const refDialogoShowProduto = useRef(null);
  const refDialogoChangeStatusRuptura = useRef(null);
  const refDialogoPrecificacao = useRef(null);
  const refDialogoImprimirCartazes = useRef(null);
  const refAreaCopiaCola = useRef(null);
  const refDialogoTesteNfceVenda = useRef(null);
  const refDialogoImagem = useRef(null);
  const refDialogoEdicaoEmMassa = useRef(null);
  const refDialogoRelatorioProdutosBalanca = useRef(null);

  function handleImprimirCartaz({ id }) {
    if (refDialogoImprimirCartazes.current) {
      refDialogoImprimirCartazes.current.handleOpen(id);
    }
  }

  function handleCriar() {
    if (refDialogoCompra.current) {
      refDialogoCompra.current.handleCreate(1);
    }
  }

  function handleEdit({ id }) {
    if (refDialogoCompra.current) {
      refDialogoCompra.current.handleEdit(id);
    }
  }

  function handleShow({ id }) {
    if (refDialogoShowProduto.current) {
      refDialogoShowProduto.current.handleOpen(id);
    }
  }

  function handleAtivar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Reativação de Produto',
        'Deseja realmente reativar este produto?',
        { id, codigo: 1 },
      );
    }
  }

  function handleDesativar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Desativação de Produto',
        'Deseja realmente desativar este produto?',
        { id, codigo: 2 },
      );
    }
  }

  function handleDeletar({ id }) {
    if (refDialogoConfirmacao.current) {
      refDialogoConfirmacao.current.handleOpen(
        'Exclusão de Produto',
        'Deseja deletar permanentemente este produto?',
        { id, codigo: 3 },
      );
    }
  }

  async function ativar(id) {
    const response = await api.put(`/produtos/status/ativar/${id}`);
    const { status, message } = response.data;

    if (status) {
      refresh();
      enqueueSnackbar('Produto ativado com sucesso!', { variant: 'success' });
    } else {
      enqueueSnackbar(message, { variant: 'error' });
    }
  }

  async function desativar(id) {
    const response = await api.put(`/produtos/status/inativar/${id}`);
    const { status, message } = response.data;

    if (status) {
      refresh();
      enqueueSnackbar('Produto inativado com sucesso!', { variant: 'success' });
    } else {
      enqueueSnackbar(message, { variant: 'error' });
    }
  }

  async function deletar(id) {
    const response = await api.put(`/produtos/status/deletar/${id}`);
    const { status, message } = response.data;

    if (status) {
      refresh();
      enqueueSnackbar('Produto deletado com sucesso!', { variant: 'success' });
    } else {
      enqueueSnackbar(message, { variant: 'error' });
    }
  }

  async function handleConfirma({ id, codigo }) {
    switch (codigo) {
      case 1:
        ativar(id);
        break;
      case 2:
        desativar(id);
        break;
      case 3:
        deletar(id);
        break;
      default:
        break;
    }
  }

  function navegarCotacoes({ id }) {
    history.push(`/cotacao-precos-produtos?produto_id=${id}`);
  }

  function navegarFluxoCaixa({ id }) {
    if (id) {
      history.push(`/relatorio-fluxo-caixa-produtos?produto_id=${id}`);
    }
  }

  function changeStatusSearch(record) {
    if (refDialogoChangeStatusSearch.current) {
      refDialogoChangeStatusSearch.current.handleOpen(record);
    }
  }

  function changeStatusRuptura(record) {
    if (refDialogoChangeStatusRuptura.current) {
      refDialogoChangeStatusRuptura.current.handleOpen(record);
    }
  }

  function handlePrecificar(record) {
    if (refDialogoPrecificacao.current) {
      refDialogoPrecificacao.current.handleOpen(record);
    }
  }

  function acumularAreaTranferencia(produto) {
    if (refAreaCopiaCola.current) {
      const { preco_venda: precoVenda } = produto;
      const precoOferta = getPrecoUnitarioVerdadeiroCaixa2(produto);
      const fator = getFator(produto);
      const labelUnidade = getUnidade(produto);
      const preco =
        precoVenda > precoOferta
          ? `${formatMoeda(precoOferta * fator)} / ${labelUnidade}`
          : `${formatMoeda(precoVenda * fator)} / ${labelUnidade}`;
      refAreaCopiaCola.current.guardar(
        produto.id,
        `${produto.nome} - ${preco}`,
      );
    }
  }

  async function handleTesteNfceProduto(record) {
    const response = await api.get(`/get-nfce-produto/${record.id}`);
    const { data } = response;
    if (refDialogoTesteNfceVenda.current) {
      refDialogoTesteNfceVenda.current.handleOpen(
        `Produto: ${record.nome}`,
        JSON.stringify(data.sefaz, null, 2),
      );
    }
  }

  async function handleDefinirImagem(record) {
    if (refDialogoImagem.current) {
      refDialogoImagem.current.handleOpen(
        record.id,
        record.link_imagem,
        record.nome,
      );
    }
  }

  function openEdicaoEmMassa() {
    if (refDialogoEdicaoEmMassa.current) {
      refDialogoEdicaoEmMassa.current.handleOpen();
    }
  }

  function openDialogoRelatorioProdutosBalanca() {
    if (refDialogoRelatorioProdutosBalanca.current) {
      refDialogoRelatorioProdutosBalanca.current.handleOpen();
    }
  }

  // Ordem importa: com `maxAcoesVisiveis={3}` no ActionsField, e ativar/desativar sendo
  // mutuamente exclusivos por status, as 4 primeiras posições garantem que sempre sobram só
  // Detalhes + Editar + (Ativar OU Desativar) como botão — o resto cai no menu "mais".
  const acoesProduto = [
    {
      key: 'show',
      title: 'Detalhes',
      icon: <VisibilityIcon />,
      onClick: handleShow,
    },
    { key: 'editar', title: 'Editar', icon: <EditIcon />, onClick: handleEdit },
    {
      key: 'ativar',
      title: 'Ativar',
      icon: <CheckCircleOutlineIcon />,
      show: (r) => r.status === STATUS_INATIVO,
      onClick: handleAtivar,
    },
    {
      key: 'desativar',
      title: 'Desativar',
      icon: <ClearIcon />,
      show: (r) => r.status === STATUS_ATIVO,
      onClick: handleDesativar,
    },
    {
      key: 'excluir',
      title: 'Excluir',
      icon: <DeleteOutlineIcon />,
      show: (r) => r.status === STATUS_INATIVO,
      onClick: handleDeletar,
    },
    {
      key: 'cotacoes',
      title: 'Cotações',
      icon: <TrendingUpIcon />,
      onClick: navegarCotacoes,
    },
    {
      key: 'estoque',
      title: 'Relatório Estoque',
      icon: <AssignmentIcon />,
      onClick: navegarFluxoCaixa,
    },
    {
      key: 'buscar',
      title: 'Incluir na lista',
      icon: IconeStatusBusca,
      onClick: changeStatusSearch,
    },
    {
      key: 'ruptura',
      title: (r) => (r.is_ruptura ? 'Em Ruptura' : 'Não Ruptura'),
      icon: IconeStatusRuptura,
      onClick: changeStatusRuptura,
    },
    {
      key: 'cartazes',
      title: 'Cartazes',
      icon: <PrintIcon />,
      onClick: handleImprimirCartaz,
    },
    {
      key: 'copiar',
      title: 'Adicionar na Área de Transferência',
      icon: <AddToPhotosIcon />,
      onClick: acumularAreaTranferencia,
    },
    {
      key: 'nfce',
      title: 'Nfce Teste Produto',
      icon: <ReceiptIcon />,
      onClick: handleTesteNfceProduto,
    },
    {
      key: 'imagem',
      title: 'Definir Imagem',
      icon: <ImageIcon />,
      onClick: handleDefinirImagem,
    },
  ];

  return (
    <div className={classes.tabelaCompacta}>
      <AreaCopiaCola ref={refAreaCopiaCola} />
      {/* O <Resource name="produtos-v2"> registrado em App.js coloca essa tela dentro de
      um ResourceContext "produtos-v2" — e useListController/useGetList priorizam esse
      contexto sobre o prop `resource` que passamos via `listProps` (ver
      ra-core/useResourceContext). Sem este provider a lista buscaria em GET /produtos-v2
      (rota que não existe no backend) em vez de GET /produtos. */}
      <ResourceContextProvider value="produtos">
        <ListPageTabbedComBuscaPagina
          title="Produtos"
          itemLabelPlural="produtos"
          perPage={50}
          rowsPerPageOptions={[10, 25, 50, 100]}
          modoRolagemTabela="setas"
          colunasFixadasIniciais={{ acoes: 'direita' }}
          larguraFixadaDireita={136}
          tabs={
            podeVerInativos ? tabs : tabs.filter((aba) => aba.id === 'ativos')
          }
          views={views}
          advancedFilters={advancedFilters}
          quickFilterSource="nome"
          quickFilterLabel="Descrição"
          quickFilterPlaceholder="Buscar por descrição..."
          onCreate={podeCriar ? handleCriar : undefined}
          createLabel="Novo"
          headerActions={
            <>
              <Tooltip title="PDF Itens de Balança">
                <IconButton
                  size="small"
                  aria-label="PDF Itens de Balança"
                  onClick={openDialogoRelatorioProdutosBalanca}
                >
                  <AssignmentIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edição em Massa">
                <IconButton
                  size="small"
                  aria-label="Edição em Massa"
                  onClick={openEdicaoEmMassa}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </>
          }
          listProps={{
            basePath,
            resource: 'produtos',
            filterDefaultValues: { status: STATUS_ATIVO, ...FILTROS_FIXOS },
          }}
        >
          <Datagrid rowStyle={postRowStyle}>
            <TextField source="id" label="Id" />
            <TextField source="nome" label="Descrição" />
            <ValorField source="preco_venda" label="Preço de Venda" />
            <FunctionField
              source="custo_final"
              label="Custo Final"
              sortable={false}
              render={(record) =>
                record.preco_custo_final > 0
                  ? `${formatMoeda(
                      record.preco_custo_final * getFator(record),
                    )}  / ${getUnidade(record)}`
                  : '-'
              }
            />
            <FunctionField
              source="margem_contribuicao"
              label="Margem de Contribuição Real (%)"
              sortable={false}
              render={(record) =>
                record.preco_custo_final > 0
                  ? `${round10(
                      ((record.preco_venda - record.preco_custo_final) /
                        record.preco_custo_final) *
                        100,
                      -2,
                    )}%`
                  : '-'
              }
            />
            <FunctionField
              source="estoque"
              label="Estoque"
              sortable={false}
              render={(record) => getEstoqueLabel(record)}
            />
            <ReferenceField
              label="Unidade"
              source="unidade_id"
              reference="unidades"
              sortable={false}
              linkType={false}
            >
              <TextField source="nome" />
            </ReferenceField>
            <ReferenceField
              label="Categoria"
              source="categoria_id"
              reference="categorias"
              sortable={false}
              linkType={false}
            >
              <TextField source="nome" />
            </ReferenceField>
            <ActionsField
              source="acoes"
              textAlign="right"
              label="Ações"
              sortable={false}
              maxAcoesVisiveis={3}
              actions={acoesProduto.filter(
                (acao) => acao.key !== 'desativar' || podeInativar,
              )}
            />
          </Datagrid>
        </ListPageTabbedComBuscaPagina>
      </ResourceContextProvider>

      <Dialogo ref={refDialogoCompra} />
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
      <DialogoChangeSearch ref={refDialogoChangeStatusSearch} />
      <DialogoShowProduto ref={refDialogoShowProduto} />
      <DialogoChangeRuptura ref={refDialogoChangeStatusRuptura} />
      <DialogoPrecificacao ref={refDialogoPrecificacao} />
      <ContainerDialogoImprimirCartazes ref={refDialogoImprimirCartazes} />
      <DialogoTesteNfceVenda ref={refDialogoTesteNfceVenda} />
      <DialogoImagem
        ref={refDialogoImagem}
        handleClose={() => {
          refresh();
        }}
      />
      <DialogoEdicaoEmMassa
        ref={refDialogoEdicaoEmMassa}
        handleClose={() => {}}
      />
      <DialogoRelatorioProdutosBalanca
        ref={refDialogoRelatorioProdutosBalanca}
      />
    </div>
  );
};

const List = (props) => (
  <ProtegerPagina permissao={PERMISSAO_TELA_PRODUTOS_LISTAR}>
    <MyList {...props} />
  </ProtegerPagina>
);

export default List;
